import { useState } from 'react';

interface TestimonialsProps {
  onOpenConsultation: () => void;
}

const REVIEWS = [
  {
    quote: 'My mother stayed for winter months to help get stronger and have people around her. They are a nice small facility and I definitely recommend them. The staff genuinely cared about her wellbeing every single day.',
    name: 'Carole M.',
    location: 'Oakland, IL',
    date: 'August 2023',
    initials: 'C',
    role: 'Daughter of Client',
    rating: 5,
  },
  {
    quote: 'My mother has been there since 2019 and absolutely loves it. The staff is very sweet and gentle with all the residents — the food is excellent! Every visit I can see how happy she is there.',
    name: 'Dea G.',
    location: 'Camargo, IL',
    date: 'July 2023',
    initials: 'D',
    role: 'Family Member',
    rating: 5,
  },
  {
    quote: 'Oaks Manor is a wonderful place. All the staff truly treat the residents as if they are their own family members. Since it\'s a smaller facility, they give so much more individual time and attention to everyone there.',
    name: 'Cassie R.',
    location: 'Newman, IL',
    date: 'April 2023',
    initials: 'C',
    role: 'Daughter of Client',
    rating: 5,
  },
  {
    quote: "It's a great place to work! The leadership is amazing, the people are so friendly and loving. You can tell they genuinely care about every resident — it truly shows in everything they do.",
    name: 'Amanda P.',
    location: 'Oakland, IL',
    date: 'May 2023',
    initials: 'A',
    role: 'Caregiver Staff',
    rating: 5,
  },
  {
    quote: 'We were nervous about in-home care at first, but Oak Senior Care completely put our family at ease. Our father\'s caregiver is patient, professional, and has become like part of our family.',
    name: 'Robert T.',
    location: 'Charleston, IL',
    date: 'March 2024',
    initials: 'R',
    role: 'Son of Client',
    rating: 5,
  },
];

function Stars({ count = 5 }: { count?: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <i key={i} className="ri-star-fill text-gold text-sm"></i>
      ))}
    </div>
  );
}

export default function Testimonials({ onOpenConsultation }: TestimonialsProps) {
  const [active, setActive] = useState(0);

  return (
    <section className="py-28 bg-offWhite relative overflow-hidden">
      {/* Subtle background */}
      <div
        className="absolute top-0 left-0 right-0 h-64 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, rgba(143,175,155,0.06) 0%, transparent 100%)' }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="inline-block w-10 h-px bg-gold opacity-70"></span>
            <p className="text-gold text-xs font-bold uppercase tracking-[0.2em]">Real Families, Real Stories</p>
            <span className="inline-block w-10 h-px bg-gold opacity-70"></span>
          </div>
          <h2 className="font-heading text-navy font-bold mb-4 leading-tight" style={{ fontSize: 'clamp(1.9rem, 3.5vw, 3.2rem)' }}>
            What Families Are Saying
          </h2>
          <div className="flex items-center justify-center gap-2.5 mt-2">
            <Stars />
            <span className="text-navy font-bold text-base">5.0</span>
            <span className="text-gray-300">·</span>
            <span className="text-gray-500 text-sm">Verified Google Reviews</span>
          </div>
        </div>

        {/* Featured testimonial */}
        <div className="max-w-3xl mx-auto mb-10">
          <div
            className="bg-white rounded-2xl p-10 relative border border-gray-100 overflow-hidden"
            style={{ boxShadow: '0 4px 30px rgba(15,42,68,0.06)' }}
          >
            {/* Quote mark */}
            <i className="ri-double-quotes-l absolute -top-2 left-5 text-9xl text-gold/8 leading-none pointer-events-none select-none"></i>
            <div className="relative z-10">
              <Stars />
              <blockquote className="font-heading text-navy leading-relaxed mt-5 mb-8 font-medium italic" style={{ fontSize: 'clamp(1.1rem, 2vw, 1.4rem)' }}>
                &ldquo;{REVIEWS[active].quote}&rdquo;
              </blockquote>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-13 h-13 flex items-center justify-center rounded-full bg-navy font-heading text-white font-bold text-lg flex-shrink-0" style={{ width: '3.25rem', height: '3.25rem' }}>
                    {REVIEWS[active].initials}
                  </div>
                  <div>
                    <p className="font-bold text-navy text-sm leading-tight">{REVIEWS[active].name}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{REVIEWS[active].role} · {REVIEWS[active].location}</p>
                    <p className="text-gray-300 text-xs">{REVIEWS[active].date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-400 bg-offWhite px-4 py-2 rounded-full border border-gray-100">
                  <i className="ri-google-fill text-[#4285F4] text-sm"></i>
                  <span>Verified Google Review</span>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation dots */}
          <div className="flex justify-center gap-2.5 mt-6">
            {REVIEWS.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${i === active ? 'bg-gold w-10' : 'bg-gray-200 w-2 hover:bg-gray-300'}`}
              />
            ))}
          </div>
        </div>

        {/* Review cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3.5 mb-12">
          {REVIEWS.map((review, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`text-left p-5 rounded-2xl border cursor-pointer transition-all ${
                i === active
                  ? 'border-gold/50 bg-white'
                  : 'border-gray-100 bg-white hover:border-sage'
              }`}
              style={i === active ? { boxShadow: '0 4px 20px rgba(200,169,106,0.12)' } : {}}
            >
              <Stars />
              <p className="text-navy text-xs leading-relaxed mt-3 mb-3.5 line-clamp-3 italic text-left">
                &ldquo;{review.quote}&rdquo;
              </p>
              <p className="text-navy font-bold text-xs text-left">{review.name}</p>
              <p className="text-gray-400 text-[10px] text-left">{review.role} · {review.location}</p>
            </button>
          ))}
        </div>

        {/* Trust + CTA bar */}
        <div
          className="flex flex-col md:flex-row items-center justify-between gap-6 bg-white rounded-2xl px-9 py-6 border border-gray-100"
          style={{ boxShadow: '0 2px 20px rgba(15,42,68,0.05)' }}
        >
          <div className="flex items-center gap-7 flex-wrap">
            <div className="flex items-center gap-2.5 text-sm text-navy">
              <i className="ri-google-fill text-[#4285F4] text-base"></i>
              <span className="font-bold">5.0</span>
              <span className="text-gray-400">on Google</span>
            </div>
            <div className="h-5 w-px bg-gray-200 hidden md:block" />
            <div className="flex items-center gap-2.5 text-sm text-navy">
              <i className="ri-facebook-fill text-[#1877F2] text-base"></i>
              <span className="font-bold">Recommended</span>
              <span className="text-gray-400">on Facebook</span>
            </div>
            <div className="h-5 w-px bg-gray-200 hidden md:block" />
            <div className="flex items-center gap-2 text-sm text-navy">
              <i className="ri-shield-check-fill text-sage text-base"></i>
              <span>Verified Reviews</span>
            </div>
          </div>
          <button
            onClick={onOpenConsultation}
            className="whitespace-nowrap bg-navy text-white font-bold px-9 py-4 rounded-full hover:bg-[#1a3d5c] transition-colors cursor-pointer text-sm flex-shrink-0"
          >
            Speak With Our Team
          </button>
        </div>
      </div>
    </section>
  );
}
